import React, { useEffect, useState, useMemo } from "react";
import supabase from "../supabase";
import { useNavigate, useParams } from "react-router-dom";
import { formatDeadline } from "../lib/formatDeadline";

export default function RequestsPage({ profile, organization }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [selectedOrg, setSelectedOrg] = useState("All");

  // Get a unique list of all organizations currently in the data
  const uniqueOrgs = useMemo(() => {
    const orgNames = requests.map(
      (req) => req.Organizations?.name || "Unknown"
    );
    return ["All", ...new Set(orgNames)].sort();
  }, [requests]);

  // Filter the requests based on the dropdown selection
  const filteredRequests = useMemo(() => {
    if (selectedOrg === "All") return requests;
    return requests.filter(
      (req) => (req.Organizations?.name || "Unknown") === selectedOrg
    );
  }, [requests, selectedOrg]);

  useEffect(() => {
    // Security check: bounce unauthorized users just in case
    if (organization?.id !== "821b771e-d2ea-4b98-8570-c59e4e14bed5") return;

    const fetchRequests = async () => {
      try {
        const { data, error } = await supabase
          .from("Requests")
          .select(
            `
            id,
            title,
            created_at,
            status,
            brief_data,
            Organizations ( name ),
            projects ( title )
          `
          )
          .order("created_at", { ascending: false });

        if (error) throw error;
        setRequests(data || []);
      } catch (err) {
        console.error("Error fetching requests:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [organization]);

  if (loading) {
    return <div style={{ padding: "40px" }}>Loading requests...</div>;
  }

  // Helper to color-code priorities
  const getPriorityStyle = (priority) => {
    if (priority === "Urgent")
      return { background: "#fee2e2", color: "#991b1b" };
    if (priority === "Low") return { background: "#f3f4f6", color: "#374151" };
    return { background: "#e0f0ee", color: "#00876e" }; // Normal
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.title}>Requests</h1>
      </div>

      <div style={styles.filterRow}>
        <label style={styles.filterLabel}>Filter by Organization:</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          style={styles.selectInput}
        >
          {uniqueOrgs.map((org) => (
            <option key={org} value={org}>
              {org}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Organization</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Project</th>
              <th style={styles.th}>Date Requested</th>
              <th style={styles.th}>Deadline</th>
              <th style={styles.th}>Priority</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => {
              const deadline = formatDeadline(req.brief_data?.deadline);
              const priority = req.brief_data?.priority || "Normal";

              return (
                <tr
                  key={req.id}
                  style={{ ...styles.tr, cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      `/organization-portal/${companyId}/requests/${req.id}`
                    )
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f9fafb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <td style={styles.tdStrong}>
                    {req.Organizations?.name || "Unknown"}
                  </td>
                  <td style={styles.td}>{req.title}</td>
                  <td style={styles.td}>
                    {req.projects?.title || "Unassigned"}
                  </td>
                  <td style={styles.td}>
                    {new Date(req.created_at).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>{deadline}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.priorityBadge,
                        ...getPriorityStyle(priority),
                      }}
                    >
                      {priority}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredRequests.length === 0 && (
              <tr>
                <td colSpan={6} style={styles.emptyState}>
                  No requests found for this organization.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "28px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#111827",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  title: { fontSize: 24, fontWeight: 800, margin: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    color: "#6b7280",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    textTransform: "uppercase",
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { padding: "16px", fontSize: 14, verticalAlign: "middle" },
  tdStrong: {
    padding: "16px",
    fontSize: 14,
    fontWeight: 800,
    verticalAlign: "middle",
    color: "#1f2937",
  },
  emptyState: { padding: 24, textAlign: "center", color: "#6b7280" },
  priorityBadge: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  filterRow: {
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  filterLabel: { fontSize: "14px", fontWeight: 700, color: "#4b5563" },
  selectInput: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    background: "#fff",
    minWidth: "200px",
    cursor: "pointer",
  },
};
